import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Get list of transfer orders with filters and pagination
export const getTransferOrders = createAsyncThunk(
  "transferOrder/getTransferOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.TRANSFER_ORDER.SLUG}?${queryString}`
      );
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get transfer order by ID with all items
export const getTransferOrderById = createAsyncThunk(
  "transferOrder/getTransferOrderById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.TRANSFER_ORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Create transfer order with header + items in single call (composite API)
export const createTransferOrderFull = createAsyncThunk(
  "transferOrder/createTransferOrderFull",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.TRANSFER_ORDER.FULL, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Update transfer order with header + items in single call (composite API)
export const updateTransferOrderFull = createAsyncThunk(
  "transferOrder/updateTransferOrderFull",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        API_URL.TRANSFER_ORDER.FULL_UPDATE(id),
        payload
      );
      dispatch(getTransferOrderById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Ship transfer order (Draft -> InTransit)
export const shipTransferOrder = createAsyncThunk(
  "transferOrder/shipTransferOrder",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.TRANSFER_ORDER.SHIP(id));
      dispatch(getTransferOrderById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Complete transfer order (InTransit -> Transferred)
export const completeTransferOrder = createAsyncThunk(
  "transferOrder/completeTransferOrder",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.TRANSFER_ORDER.COMPLETE(id));
      dispatch(getTransferOrderById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Cancel transfer order (Draft -> Cancelled)
export const cancelTransferOrder = createAsyncThunk(
  "transferOrder/cancelTransferOrder",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.TRANSFER_ORDER.CANCEL(id));
      dispatch(getTransferOrderById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Delete transfer order (soft delete, Draft only)
export const deleteTransferOrder = createAsyncThunk(
  "transferOrder/deleteTransferOrder",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.TRANSFER_ORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

// Get next transfer number
export const getNextTransferNumber = createAsyncThunk(
  "transferOrder/getNextTransferNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.TRANSFER_ORDER.GET_NEXT_TRANSFER_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get active locations for dropdown
export const getActiveLocations = createAsyncThunk(
  "transferOrder/getActiveLocations",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.LOCATION.ACTIVE_LOCATIONS);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get warehouses by location (for source)
export const getWarehousesByLocation = createAsyncThunk(
  "transferOrder/getWarehousesByLocation",
  async (locationId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.WAREHOUSE.LOOKUP}?LocationId=${locationId}&IsActive=true`
      );
      return data.data?.list || data.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get warehouses by location (for destination - separate state)
export const getDestWarehousesByLocation = createAsyncThunk(
  "transferOrder/getDestWarehousesByLocation",
  async (locationId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.WAREHOUSE.LOOKUP}?LocationId=${locationId}&IsActive=true`
      );
      return data.data?.list || data.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get bins by warehouse (for source)
export const getSourceBinsByWarehouse = createAsyncThunk(
  "transferOrder/getSourceBinsByWarehouse",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.WAREHOUSE.BINS_LOOKUP(warehouseId));
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get bins by warehouse (for destination)
export const getDestBinsByWarehouse = createAsyncThunk(
  "transferOrder/getDestBinsByWarehouse",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.WAREHOUSE.BINS_LOOKUP(warehouseId));
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get active items for dropdown with pagination support
export const getActiveItems = createAsyncThunk(
  "transferOrder/getActiveItems",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (payload?.searchText) params.append("SearchText", payload.searchText);
      if (payload?.pageNumber) params.append("PageNumber", payload.pageNumber);
      if (payload?.pageSize) params.append("PageSize", payload.pageSize);
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.ITEM.ACTIVE_ITEMS_LOOKUP}${queryString ? `?${queryString}` : ""}`
      );
      // Return both data and append flag
      return {
        ...data.data,
        appendItems: payload?.pageNumber > 1, // Append if not first page
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Fetch stock available at source location/warehouse (legacy)
export const fetchStockAvailable = createAsyncThunk(
  "transferOrder/fetchStockAvailable",
  async ({ itemId, locationId, warehouseId }, { rejectWithValue }) => {
    try {
      const payload = {
        itemId,
        locationId,
        warehouseId: warehouseId || null,
      };
      const { data } = await axios.post(
        API_URL.TRANSFER_ORDER.STOCK_AVAILABLE,
        payload
      );
      return { itemId, stockData: data.data || [] };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get unified transfer locations (Business Locations + Warehouses in hierarchical order)
export const getTransferLocations = createAsyncThunk(
  "transferOrder/getTransferLocations",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.TRANSFER_ORDER.TRANSFER_LOCATIONS);
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get source stock breakdown (by bin for bin-enabled warehouses)
export const getSourceStockBreakdown = createAsyncThunk(
  "transferOrder/getSourceStockBreakdown",
  async ({ itemId, locationId, locationType }, { rejectWithValue }) => {
    try {
      const payload = {
        itemId,
        locationId,
        locationType, // 1 = BusinessLocation, 2 = Warehouse
      };
      const { data } = await axios.post(
        API_URL.TRANSFER_ORDER.SOURCE_STOCK_BREAKDOWN,
        payload
      );
      return { itemId, stockBreakdown: data.data || [] };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get destination bins (for bin-enabled warehouses).
// Accepts pagination + search params so the dropdown can load bins lazily via
// `useSelectPagination`. Backend still returns the full list when PageNumber
// / PageSize are omitted (backward compat).
export const getDestinationBins = createAsyncThunk(
  "transferOrder/getDestinationBins",
  async (payload, { rejectWithValue }) => {
    try {
      const {
        itemId,
        locationId,
        locationType,
        PageNumber,
        PageSize,
        searchText,
      } = payload || {};

      const body = {
        itemId,
        locationId,
        locationType, // 1 = BusinessLocation, 2 = Warehouse
      };
      if (PageNumber) body.pageNumber = PageNumber;
      if (PageSize) body.pageSize = PageSize;
      if (searchText) body.search = searchText;

      const response = await axios.post(
        API_URL.TRANSFER_ORDER.DESTINATION_BINS,
        body,
      );
      const responseData = response?.data?.data;

      // Non-paginated response is a plain array (backward compat for legacy callers)
      if (Array.isArray(responseData)) {
        return {
          itemId,
          list: responseData,
          totalPages: 1,
          nextPageNumber: 1,
          appendBins: false,
        };
      }

      // Paginated response: { list, pageNumber, pageSize, totalRecords, totalPages, nextPageNumber, hasNextPage }
      return {
        ...(responseData || {}),
        itemId,
        list: responseData?.list || [],
        appendBins: (PageNumber || 1) > 1,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  list: [],
  detail: null,
  current_values: null,
  active_payload: null,
  total_records: 0,
  isLoading: false,
  // Dropdowns (legacy - kept for backward compatibility)
  locations: [],
  warehouses: [], // Source warehouses
  destWarehouses: [], // Destination warehouses
  sourceBins: [],
  destBins: [],
  items: [],
  // New unified transfer locations
  transferLocations: [], // Combined Business Locations + Warehouses
  transferLocations_loading: false,
  // Source stock breakdown (keyed by itemId)
  sourceStockBreakdown: {},
  sourceStockBreakdown_loading: false,
  // Destination bins (keyed by itemId — legacy cache; kept for any non-dropdown consumers)
  destinationBinsMap: {},
  // Flat destination bins list used by the paginated + searchable dropdown.
  destinationBins: [],
  destinationBins_loading: false,
  // Loading states (legacy)
  locations_loading: false,
  warehouses_loading: false,
  destWarehouses_loading: false,
  sourceBins_loading: false,
  destBins_loading: false,
  items_loading: false,
  // Stock available at source (legacy)
  stockAvailable: {},
  stockAvailable_loading: false,
};

const transferOrderSlice = createSlice({
  name: "transferOrder",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.list = [];
    },
    resetDetail(state) {
      state.detail = null;
    },
    resetWarehouses(state) {
      state.warehouses = [];
    },
    resetDestWarehouses(state) {
      state.destWarehouses = [];
    },
    resetSourceBins(state) {
      state.sourceBins = [];
    },
    resetDestBins(state) {
      state.destBins = [];
    },
    resetItems(state) {
      state.items = [];
    },
    resetStockAvailable(state) {
      state.stockAvailable = {};
    },
    // New reducers for unified transfer locations
    resetTransferLocations(state) {
      state.transferLocations = [];
    },
    resetSourceStockBreakdown(state) {
      state.sourceStockBreakdown = {};
    },
    resetDestinationBinsMap(state) {
      state.destinationBinsMap = {};
    },
    resetDestinationBins(state) {
      state.destinationBins = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get list
      .addCase(getTransferOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getTransferOrders.fulfilled, (state, action) => {
        state.list = action.payload.list;
        state.total_records = action.payload.data?.totalRecords || 0;
        state.isLoading = false;
      })
      .addCase(getTransferOrders.rejected, (state) => {
        state.isLoading = false;
      })
      // Get by ID
      .addCase(getTransferOrderById.fulfilled, (state, action) => {
        state.detail = action.payload;
      })
      // Locations
      .addCase(getActiveLocations.pending, (state) => {
        state.locations_loading = true;
      })
      .addCase(getActiveLocations.fulfilled, (state, action) => {
        state.locations = action.payload || [];
        state.locations_loading = false;
      })
      .addCase(getActiveLocations.rejected, (state) => {
        state.locations_loading = false;
      })
      // Source Warehouses
      .addCase(getWarehousesByLocation.pending, (state) => {
        state.warehouses_loading = true;
      })
      .addCase(getWarehousesByLocation.fulfilled, (state, action) => {
        state.warehouses = action.payload || [];
        state.warehouses_loading = false;
      })
      .addCase(getWarehousesByLocation.rejected, (state) => {
        state.warehouses_loading = false;
      })
      // Destination Warehouses
      .addCase(getDestWarehousesByLocation.pending, (state) => {
        state.destWarehouses_loading = true;
      })
      .addCase(getDestWarehousesByLocation.fulfilled, (state, action) => {
        state.destWarehouses = action.payload || [];
        state.destWarehouses_loading = false;
      })
      .addCase(getDestWarehousesByLocation.rejected, (state) => {
        state.destWarehouses_loading = false;
      })
      // Source Bins
      .addCase(getSourceBinsByWarehouse.pending, (state) => {
        state.sourceBins_loading = true;
      })
      .addCase(getSourceBinsByWarehouse.fulfilled, (state, action) => {
        state.sourceBins = action.payload || [];
        state.sourceBins_loading = false;
      })
      .addCase(getSourceBinsByWarehouse.rejected, (state) => {
        state.sourceBins_loading = false;
      })
      // Destination Bins
      .addCase(getDestBinsByWarehouse.pending, (state) => {
        state.destBins_loading = true;
      })
      .addCase(getDestBinsByWarehouse.fulfilled, (state, action) => {
        state.destBins = action.payload || [];
        state.destBins_loading = false;
      })
      .addCase(getDestBinsByWarehouse.rejected, (state) => {
        state.destBins_loading = false;
      })
      // Items
      .addCase(getActiveItems.pending, (state) => {
        state.items_loading = true;
      })
      .addCase(getActiveItems.fulfilled, (state, action) => {
        const newItems = action.payload?.list || [];
        if (action.payload?.appendItems) {
          // Append new items, avoiding duplicates
          const existingIds = new Set(state.items.map((item) => item.id));
          const uniqueNewItems = newItems.filter((item) => !existingIds.has(item.id));
          state.items = [...state.items, ...uniqueNewItems];
        } else {
          // Replace items (first page or new search)
          state.items = newItems;
        }
        state.items_loading = false;
      })
      .addCase(getActiveItems.rejected, (state) => {
        state.items_loading = false;
      })
      // Stock available
      .addCase(fetchStockAvailable.pending, (state) => {
        state.stockAvailable_loading = true;
      })
      .addCase(fetchStockAvailable.fulfilled, (state, action) => {
        state.stockAvailable[action.payload.itemId] = action.payload.stockData;
        state.stockAvailable_loading = false;
      })
      .addCase(fetchStockAvailable.rejected, (state) => {
        state.stockAvailable_loading = false;
      })
      // Transfer Locations (unified)
      .addCase(getTransferLocations.pending, (state) => {
        state.transferLocations_loading = true;
      })
      .addCase(getTransferLocations.fulfilled, (state, action) => {
        state.transferLocations = action.payload || [];
        state.transferLocations_loading = false;
      })
      .addCase(getTransferLocations.rejected, (state) => {
        state.transferLocations_loading = false;
      })
      // Source Stock Breakdown
      .addCase(getSourceStockBreakdown.pending, (state) => {
        state.sourceStockBreakdown_loading = true;
      })
      .addCase(getSourceStockBreakdown.fulfilled, (state, action) => {
        state.sourceStockBreakdown[action.payload.itemId] = action.payload.stockBreakdown;
        state.sourceStockBreakdown_loading = false;
      })
      .addCase(getSourceStockBreakdown.rejected, (state) => {
        state.sourceStockBreakdown_loading = false;
      })
      // Destination Bins
      .addCase(getDestinationBins.pending, (state) => {
        state.destinationBins_loading = true;
      })
      .addCase(getDestinationBins.fulfilled, (state, action) => {
        const newBins = action.payload?.list || [];
        if (action.payload?.appendBins) {
          const existingIds = new Set(
            state.destinationBins.map((b) => b.binId),
          );
          const uniqueNew = newBins.filter(
            (b) => !existingIds.has(b.binId),
          );
          state.destinationBins = [...state.destinationBins, ...uniqueNew];
        } else {
          state.destinationBins = newBins;
        }
        // Keep legacy map in sync for any stale consumers that still read it.
        if (action.payload?.itemId) {
          state.destinationBinsMap[action.payload.itemId] = state.destinationBins;
        }
        state.destinationBins_loading = false;
      })
      .addCase(getDestinationBins.rejected, (state) => {
        state.destinationBins_loading = false;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetDetail,
  resetWarehouses,
  resetDestWarehouses,
  resetSourceBins,
  resetDestBins,
  resetItems,
  resetStockAvailable,
  resetTransferLocations,
  resetSourceStockBreakdown,
  resetDestinationBinsMap,
  resetDestinationBins,
} = transferOrderSlice.actions;

export default transferOrderSlice.reducer;
